/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  BsTelephoneFill, BsEnvelopeFill, BsGeoAltFill, BsSendFill, BsCheckCircleFill, BsExclamationTriangleFill 
} from 'react-icons/bs';

export default function ContactSection() {
  const formRef = useRef();
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setErrorMessage("");
    setIsSuccess(false);

    const endpoint = 'https://www.apply.mahakalsocialwelfaresociety.in/index.php?rest_route=/contact-form-7/v1/contact-forms/150/feedback';
    const formData = new FormData(formRef.current);
    
    formData.append('_wpcf7', '150');
    formData.append('_wpcf7_unit_tag', 'wpcf7-f150-p1-o1');
    formData.append('_wpcf7_container_post', '0');

    try {
      const response = await fetch(endpoint, { method: 'POST', body: formData });
      const result = await response.json();

      if (result.status === 'mail_sent') {
        setIsSuccess(true);
        formRef.current.reset();
        setTimeout(() => setIsSuccess(false), 5000);
      } else {
        setErrorMessage(result.message || "Something went wrong.");
      }
    } catch (err) {
      setErrorMessage("Network error. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
   <section className="relative min-h-screen bg-[#fffcf9] py-20 px-4 overflow-hidden">
  {/* Background Decor */}
  <div className="absolute top-0 -left-20 w-96 h-96 bg-orange-100 rounded-full mix-blend-multiply filter blur-3xl opacity-50" />
  <div className="absolute bottom-0 -right-20 w-96 h-96 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30" />

  <div className="relative mx-auto max-w-6xl">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
      
      {/* Left Side: Info */}
      <div className="lg:col-span-5 pt-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-orange-600 font-bold tracking-widest uppercase text-xs">Contact Us</span>
          <h2 className="text-5xl font-black text-gray-900 mt-4 mb-8 leading-tight">
            Ready to start? <br/> 
            <span className="text-orange-500">Get in touch.</span>
          </h2>
          
          <div className="space-y-8">
            <ContactInfoItem 
              icon={<BsTelephoneFill />} 
              title="Phone" 
              detail={
                <div className="flex flex-col md:flex-row md:gap-2">
                  <a href="tel:+918827529117" className="hover:text-orange-500 transition-colors duration-200">+91 8827529117</a>
                  <span className="hidden md:inline text-gray-400">/</span>
                  <a href="tel:+917723024874" className="hover:text-orange-500 transition-colors duration-200">772-302-4874</a>
                </div>
              } 
            />
            
            <ContactInfoItem 
              icon={<BsEnvelopeFill />} 
              title="Email" 
              detail={
                <a href="mailto:lakhansinghdikshit@gmail.com" className="hover:text-orange-500 transition-colors duration-200 break-all">
                  lakhansinghdikshit@gmail.com
                </a>
              } 
            />
            
            <ContactInfoItem 
              icon={<BsGeoAltFill />} 
              title="Office" 
              detail={
                <a 
                  href="#" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-orange-500 transition-colors duration-200 block"
                >
                  Village Pacharahi, Post Baijalpur Bodla kawardha, India - Pin 491995
                </a>
              } 
            />
          </div>
        </motion.div>
      </div>

      {/* Right Side: Form with Fixed Labels */}
      <motion.div 
        className="lg:col-span-7 bg-white p-8 md:p-10 rounded-[2.5rem] shadow-2xl shadow-orange-900/5 border border-orange-50"
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
      >
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ModernInput label="Full Name" name="full-name" type="text" placeholder="e.g. John Doe" required />
            <ModernInput label="Contact No" name="contact-no" type="tel" placeholder="+91 ..." required />
          </div>

          <ModernInput label="Email Address" name="email-id" type="email" placeholder="example@mail.com" required />
          
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-gray-500 ml-1">
              <span className="w-1.5 h-1.5 bg-orange-500 rounded-full"></span>
              Brief Message
            </label>
            <textarea
              name="your-message"
              required
              rows="4"
              className="w-full bg-gray-50 border-2 border-gray-300 focus:border-orange-500 focus:bg-white rounded-2xl p-4 outline-none transition-all duration-300 text-gray-900 font-medium placeholder:text-gray-300"
              placeholder="How can we help you?"
            />
          </div>

          <AnimatePresence>
            {errorMessage && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-red-500 text-sm font-semibold flex items-center gap-2">
                <BsExclamationTriangleFill /> {errorMessage}
              </motion.div>
            )}
          </AnimatePresence>

          <button
            disabled={isSending}
            type="submit"
            className={`w-full py-5 rounded-2xl font-bold text-white transition-all duration-300 shadow-lg flex items-center justify-center gap-3
              ${isSuccess ? 'bg-green-500' : 'bg-gray-900 hover:bg-orange-600 hover:-translate-y-1'}`}
          >
            {isSending ? (
              <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : isSuccess ? (
              <><BsCheckCircleFill /> SUCCESS! SENT</>
            ) : (
              <><BsSendFill /> SEND MESSAGE</>
            )}
          </button>
        </form>
      </motion.div>
    </div>
  </div>
</section>
  );
}

function ContactInfoItem({ icon, title, detail }) {
  return (
    <div className="flex items-center gap-5">
      <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center text-orange-600 shadow-md border border-orange-50">
        {icon}
      </div>
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter">{title}</p>
        <p className="text-lg font-bold text-gray-800">{detail}</p>
      </div>
    </div>
  );
}

/* --- THE FIXED LABEL INPUT COMPONENT --- */
function ModernInput({ label, name, ...props }) {
  return (
    <div className="flex flex-col gap-2 w-full group">
      {/* Label is now fixed above the input */}
      <label className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-gray-500 ml-1 transition-colors group-focus-within:text-orange-600">
        <span className="w-1.5 h-1.5 bg-orange-400 rounded-full group-focus-within:bg-orange-600 transition-colors"></span>
        {label}
      </label>
      
      <input
        {...props}
        name={name}
        className="w-full h-14 bg-gray-50 border-2 border-gray-300 focus:border-orange-500 focus:bg-white rounded-2xl px-5 outline-none transition-all duration-300 text-gray-900 font-semibold placeholder:text-gray-300"
      />
    </div>
  );
}